import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../i18n';
import { Logo } from '../components/Logo';
import { Reveal } from '../components/Reveal';
import { Overline } from '../components/Overline';
import { CTASection } from '../components/CTASection';

const Home = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pt-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 flex max-w-4xl flex-col items-center text-center"
        >
          <Logo className="h-14 w-auto md:h-16" testId="hero-logo" />

          <p
            className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-steel-label"
            data-testid="hero-overline"
          >
            {t.home.overline}
          </p>

          <h1
            className="mt-6 text-4xl font-bold leading-[1.04] tracking-tighter text-silver sm:text-6xl lg:text-7xl"
            data-testid="hero-title"
          >
            {t.home.titleLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h1>

          <p
            className="mt-8 max-w-xl text-base leading-relaxed text-steel-light"
            data-testid="hero-subtitle"
          >
            {t.home.subtitle}
          </p>

          <div className="mt-10 flex w-full flex-col gap-4 sm:w-auto sm:flex-row">
            <Link
              to="/contato"
              data-testid="hero-cta-primary"
              className="group inline-flex w-full items-center justify-center gap-3 rounded-sm bg-silver px-7 py-3.5 text-sm font-medium text-graphite transition-colors duration-200 hover:bg-silver-glow sm:w-auto"
            >
              {t.home.ctaPrimary}
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/servicos"
              data-testid="hero-cta-secondary"
              className="inline-flex w-full items-center justify-center rounded-sm bg-white/[0.06] px-7 py-3.5 text-sm font-medium text-silver-dim transition-colors duration-200 hover:bg-white/[0.1] hover:text-silver-glow sm:w-auto"
            >
              {t.home.ctaSecondary}
            </Link>
          </div>
        </motion.div>
      </section>

      <section className="border-t border-graphite-200" data-testid="home-what-section">
        <div className="mx-auto max-w-7xl px-6 py-28">
          <Reveal>
            <Overline>{t.home.pillars.overline}</Overline>
          </Reveal>

          <div className="mt-10 grid gap-12 md:grid-cols-2 md:gap-16">
            {t.home.pillars.items.map((pillar, i) => (
              <div
                key={pillar.title}
                className={i > 0 ? 'md:border-l md:border-graphite-200 md:pl-16' : ''}
                data-testid={`home-pillar-${i + 1}`}
              >
                <h2 className="text-2xl font-semibold tracking-tight text-silver sm:text-3xl">
                  {pillar.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-steel-light">{pillar.body}</p>
                <p className="mt-4 text-sm leading-relaxed text-steel">{pillar.list}</p>
              </div>
            ))}
          </div>

          <Link
            to="/servicos"
            data-testid="home-what-link"
            className="group mt-14 inline-flex items-center gap-2 text-sm text-silver-dim transition-colors duration-200 hover:text-silver-glow"
          >
            {t.home.pillars.link}
            <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Home;
